import { useState } from "react";
import { useAccount } from "@/hooks/useAccount";
import { fmtCurrency, fmtPct, fmtDate } from "@/lib/api";
import PerformanceChart from "@/components/PerformanceChart";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowUpRight, ArrowDownRight } from "lucide-react";

const GRANULARITY = {
  day: { key: "daily", label: "Day", xMode: "day" },
  week: { key: "weekly", label: "Week", xMode: "day" },
  month: { key: "monthly", label: "Month", xMode: "month" },
  year: { key: "yearly", label: "Year", xMode: "year" },
};

function ChangeCard({ label, change }) {
  if (!change) return null;
  const pos = change.abs >= 0;
  return (
    <Card className="p-5 bg-white border-slate-200 shadow-[0_2px_8px_-2px_rgba(15,23,42,0.08)]">
      <p className="overline mb-2">{label}</p>
      <p className={`text-2xl font-bold tabular-nums ${pos ? "text-[#059669]" : "text-[#dc2626]"}`}>
        {pos ? "+" : ""}{fmtCurrency(change.abs)}
      </p>
      <p className={`text-sm font-medium tabular-nums flex items-center gap-1 mt-1 ${pos ? "text-[#059669]" : "text-[#dc2626]"}`}>
        {pos ? <ArrowUpRight className="w-4 h-4" strokeWidth={2} /> : <ArrowDownRight className="w-4 h-4" strokeWidth={2} />}
        {fmtPct(change.pct)}
      </p>
    </Card>
  );
}

export default function Performance() {
  const { data, isLoading } = useAccount();
  const [gran, setGran] = useState("month");

  if (isLoading) return <div className="space-y-6"><Skeleton className="h-10 w-64" /><Skeleton className="h-96 rounded-2xl" /></div>;

  const perf = data?.performance || {};
  const stats = perf.stats || {};
  const active = GRANULARITY[gran];
  const series = perf[active.key] || [];
  const returns = [...(data?.daily_returns || [])].reverse();

  return (
    <div className="space-y-8" data-testid="performance-page">
      <div className="fade-up">
        <p className="overline mb-1">Analytics</p>
        <h1 className="font-serif text-4xl font-bold text-slate-900">Performance</h1>
        <p className="text-slate-500 mt-1">Every return, day-wise — grouped by day, week, month or year.</p>
      </div>

      <div className="grid gap-6 sm:grid-cols-3 fade-up">
        <ChangeCard label="Today" change={stats.day} />
        <ChangeCard label="This Week" change={stats.week} />
        <ChangeCard label="This Month" change={stats.month} />
      </div>

      <Card className="p-6 bg-white border-slate-200 shadow-[0_2px_8px_-2px_rgba(15,23,42,0.08)] fade-up">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
          <div>
            <h3 className="font-serif text-2xl font-bold text-slate-900">Account value</h3>
            <p className="text-sm text-slate-400">{series.length} points · viewing by {active.label.toLowerCase()}</p>
          </div>
          <Select value={gran} onValueChange={setGran}>
            <SelectTrigger className="w-40 rounded-full bg-white" data-testid="granularity-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="day" data-testid="gran-day">Day</SelectItem>
              <SelectItem value="week" data-testid="gran-week">Week</SelectItem>
              <SelectItem value="month" data-testid="gran-month">Month</SelectItem>
              <SelectItem value="year" data-testid="gran-year">Year</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {series.length ? (
          <PerformanceChart data={series} xMode={active.xMode} height={360} />
        ) : (
          <p className="text-slate-400 text-sm py-16 text-center">No {active.label.toLowerCase()} data available.</p>
        )}
      </Card>

      {returns.length > 0 && (
        <Card className="p-6 bg-white border-slate-200 shadow-[0_2px_8px_-2px_rgba(15,23,42,0.08)] fade-up" data-testid="all-returns">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-serif text-2xl font-bold text-slate-900">All daily returns</h3>
            <span className="text-sm text-slate-400 tabular-nums">{returns.length} days</span>
          </div>
          <ScrollArea className="h-[360px] pr-3">
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
              {returns.map((r, i) => {
                const pos = r.amount >= 0;
                return (
                  <div key={`${r.date}-${i}`} data-testid={`return-${i}`} className="flex items-center justify-between px-4 py-2.5 rounded-xl bg-slate-50">
                    <span className="text-sm text-slate-500 tabular-nums">{fmtDate(r.date)}</span>
                    <span className={`text-sm font-semibold tabular-nums ${pos ? "text-[#059669]" : "text-[#dc2626]"}`}>
                      {pos ? "+" : ""}{fmtCurrency(r.amount)}
                    </span>
                  </div>
                );
              })}
            </div>
          </ScrollArea>
        </Card>
      )}
    </div>
  );
}
